import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { EPackages } from '@feathr/blackbox';
import { EIntegrationTypes } from '@feathr/blackbox';
import {
  useGoogleAdsPermissions,
  useLicense,
  useMetaIntegrationStatus,
} from '@feathr/extender/hooks';
import { useAccount, useFlags } from '@feathr/extender/state';

import type { IBaseIntegrationCardProps } from './BaseIntegrationsCard';
import IntegrationsCard from './IntegrationsCard';

import blackbaudLogo from 'images/integrations/blackbaud.png';
import googleAdsLogo from 'images/integrations/google_ads.png';
import iMISLogo from 'images/integrations/iMIS.png';
import metaLogo from 'images/zapier_brands/meta.png';
import virtuousLogo from 'images/zapier_brands/virtuous.png';

export interface INativeIntegrationCardProps {
  logo?: string;
  name: string;
  /** The primary URL of the card. */
  url: string;
  /** Hide the card. For example, a card should be hidden when the account doesn't have a Pixel implemented. */
  isCardHidden?: boolean;
  props?: Partial<IBaseIntegrationCardProps>;
}

enum EIntegrationsNames {
  iMIS = 'iMIS',
  BlackbaudRaisersEdge = "Blackbaud Raiser's Edge NXT",
}

function NativeIntegrations(): JSX.Element {
  const account = useAccount();
  const { t } = useTranslation();
  const {
    iMISDirectIntegration,
    blackbaudDirectIntegration,
    hasApiKeys,
    googleAdsSmart: hasGoogleAdsSmart,
    implementationsPageVisible,
    facebook: hasFacebook,
  } = useLicense([
    'iMISDirectIntegration',
    'blackbaudDirectIntegration',
    'hasApiKeys',
    'googleAdsSmart',
    'implementationsPageVisible',
    'facebook',
  ]);
  const {
    hasImis: hasImisFlag,
    hasBlackbaudRaisersEdge: hasBlackbaudRaisersEdgeFlag,
    virtuous: hasVirtuousFlag,
  } = useFlags();

  const showImis = iMISDirectIntegration || hasImisFlag;
  const showBlackbaud = blackbaudDirectIntegration || hasBlackbaudRaisersEdgeFlag;
  const googleAdsPermissions = useGoogleAdsPermissions({ requireConnectedCustomer: false });
  const showGoogleAds = googleAdsPermissions || hasGoogleAdsSmart;
  const metaIntegrationStatus = useMetaIntegrationStatus();

  const isImisConflicting = account.conflictingIntegrations.includes(EIntegrationTypes.Imis);
  const isBlackbaudConflicting = account.conflictingIntegrations.includes(
    EIntegrationTypes.BlackbaudRaisersEdge,
  );

  /**
   * Returns a message indicating that the user should upgrade to a specific package to access a feature.
   * @param packageName The name of the package to upgrade to.
   * @returns A string message indicating the upgrade requirement.
   */
  function upgradeToUseMessage(packageName: EPackages): string {
    return t('Upgrade to {{packageName}} to access this feature.', { packageName });
  }

  /**
   * Returns a message indicating that the user has a conflicting integration and must disconnect from it to connect to another integration.
   * @param integrationName The name of the conflicting integration.
   * @param newIntegration The name of the new integration.
   * @returns A string message indicating the conflicting integration.
   */
  function getConflictingIntegrationMessage(
    integrationName: EIntegrationsNames,
    newIntegration: EIntegrationsNames,
  ): string {
    return t(
      'You have a conflicting integration. You must disconnect from {{- integrationName}} to be able to connect to {{- newIntegration}}.',
      {
        integrationName,
        newIntegration,
      },
    );
  }

  function iMISIntegrationMessage(): string | undefined {
    if (showImis && !isImisConflicting) {
      return undefined;
    }

    if (isImisConflicting) {
      return getConflictingIntegrationMessage(
        EIntegrationsNames.BlackbaudRaisersEdge,
        EIntegrationsNames.iMIS,
      );
    }
    if (!iMISDirectIntegration) {
      return upgradeToUseMessage(EPackages.FeathrPlatformAdvanced);
    }
    return undefined;
  }

  function blackbaudIntegrationMessage(): string | undefined {
    if (showBlackbaud || blackbaudDirectIntegration) {
      return undefined;
    }
    if (isBlackbaudConflicting) {
      return getConflictingIntegrationMessage(
        EIntegrationsNames.iMIS,
        EIntegrationsNames.BlackbaudRaisersEdge,
      );
    }
    if (!blackbaudDirectIntegration) {
      return upgradeToUseMessage(EPackages.FeathrPlatformAdvanced);
    }
    return undefined;
  }

  function metaIntegrationMessage(): string | undefined {
    if (!metaIntegrationStatus.isDisabled) {
      return undefined;
    }
    return t(metaIntegrationStatus.disabledMessage);
  }

  const nativeIntegrations: INativeIntegrationCardProps[] = [
    {
      name: 'API Keys',
      url: '/settings/integrations/api-keys',
      isCardHidden: !hasApiKeys,
    },
    {
      name: "Blackbaud Raiser's Edge NXT",
      logo: blackbaudLogo,
      url: '/settings/integrations/raisers-edge',
      props: {
        tooltip: blackbaudIntegrationMessage(),
        disabled: !showBlackbaud || isBlackbaudConflicting,
      },
    },
    {
      name: 'Forms / Implementations',
      url: '/data/pixel/implementations',
      isCardHidden: !implementationsPageVisible,
    },
    {
      name: 'Google Ads',
      logo: googleAdsLogo,
      url: '/settings/integrations/google-ads',
      props: {
        disabled: !showGoogleAds,
        tooltip: !showGoogleAds && upgradeToUseMessage(EPackages.FeathrPlatformStarter),
      },
    },
    {
      name: 'iMIS',
      logo: iMISLogo,
      url: '/settings/integrations/imis',
      props: {
        tooltip: iMISIntegrationMessage(),
        disabled: !showImis || isImisConflicting,
      },
    },
    {
      name: 'Meta',
      logo: metaLogo,
      url: '/settings/integrations/meta',
      props: {
        tooltip: metaIntegrationMessage(),
        disabled: !hasFacebook || metaIntegrationStatus.isDisabled,
      },
    },
    {
      name: 'Virtuous',
      logo: virtuousLogo,
      url: '/settings/integrations/virtuous',
      isCardHidden: !hasVirtuousFlag,
    },
  ];

  return (
    <>
      {nativeIntegrations
        .filter((integration) => !integration.isCardHidden)
        .map((integration) => (
          <IntegrationsCard {...integration.props} item={integration} key={integration.name} />
        ))}
    </>
  );
}

export default NativeIntegrations;
